import { Injectable } from '@angular/core';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CustomHttpErrorResponse, Error } from '../../../../../common';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { AllocationViewActionsFacade } from '../actions';
import { AllocationViewActionIdentifierTypeEnum } from '../constants';
import { ActionBriefModel, AllocationViewSlotBriefModel, AllocationViewSlotBriefUliInfoModel } from '../models';
import { BriefLocationInfoModel } from '../models/allocation-brief-models';
import { AllocationViewSlotBriefInitialState, AllocationViewSlotBriefLoad } from './allocation-view-slot-brief.actions';
import { AllocationViewSlotBriefService } from './allocation-view-slot-brief.service';

export interface AllocationViewSlotBriefStateModel {
  slot: AllocationViewSlotBriefModel;
  actionBriefDetails: ActionBriefModel;
  locationInfo: BriefLocationInfoModel;
  loaded: boolean;
}

const initialState: AllocationViewSlotBriefStateModel = {
  slot: null,
  actionBriefDetails: null,
  locationInfo: null,
  loaded: false,
};

@State({
  name: 'allocationViewSlotBrief',
  defaults: initialState,
})
@Injectable()
export class AllocationViewSlotBriefState {
  constructor(
    private allocationViewSlotInfoService: AllocationViewSlotBriefService,
    private allocationViewActionsFacade: AllocationViewActionsFacade,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getLoaded(state: AllocationViewSlotBriefStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getSlotBrief(state: AllocationViewSlotBriefStateModel): AllocationViewSlotBriefModel {
    return state.slot;
  }

  @Selector()
  static getLocationInfo(state: AllocationViewSlotBriefStateModel): BriefLocationInfoModel {
    return { compartmentId: state.slot.compartmentId, dockId: state.slot.dockId };
  }

  @Selector()
  static getActionBriefDetails(state: AllocationViewSlotBriefStateModel): ActionBriefModel {
    return state.actionBriefDetails;
  }

  @Action(AllocationViewSlotBriefLoad)
  getSlotBrief(
    ctx: StateContext<AllocationViewSlotBriefStateModel>,
    { payload: { compartmentId, slotId } }: AllocationViewSlotBriefLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.allocationViewSlotInfoService.getSlotBrief(compartmentId, slotId).pipe(
      map((slot: AllocationViewSlotBriefModel) => {
        const actionBriefDetails = new ActionBriefModel({
          hubId: slot.hubId,
          truckId: slot.truckInfo.truckId,
          slotId: slot.slotId,
          compartmentId: slot.compartmentId,
          actionOrigin: AllocationViewActionIdentifierTypeEnum.Slot,
          uli: slot.uliInfos?.length ? slot.uliInfos[0].uli : null,
          type: slot.uliInfos?.length ? slot.uliInfos[0].type : null,
        });

        this.allocationViewActionsFacade.setLeadUlis(
          slot.uliInfos.map((uli: AllocationViewSlotBriefUliInfoModel) => ({
            uli: uli?.uli,
            activityTpe: uli?.type,
            compartmentId,
          })),
        );
        ctx.patchState({ slot, actionBriefDetails, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AllocationViewSlotBriefInitialState)
  setInitialState(ctx: StateContext<AllocationViewSlotBriefStateModel>): void {
    ctx.setState(initialState);
  }
}
